import { IPersonalInfo } from '../../../../../types';
import { StudentState } from '../../../../../store/student/types';
import { AppliedEUSSStatus, IDataCountry } from 'api-paths-cas';
import { countries } from 'platform-data';
type Fields = Pick<
  IPersonalInfo,
  | 'gender'
  | 'dob'
  | 'birthCountry'
  | 'nationality'
  | 'appliedForEUSS'
  | 'grantedEUSSStatus'
  | 'applyingFromHomeCountry'
  | 'countryOfVisaApplication'
  | 'livedInUKLast12Months'
  | 'otherCountryReason'
  | 'hasOtherNationality'
  | 'otherNationalities'
>;

export const Visible = {} as Record<
  | 'gender'
  | 'dob'
  | 'birthCountry'
  | 'nationality'
  | 'appliedForEUSS'
  | 'grantedEUSSStatus'
  | 'isCountryOfNationality'
  | 'countryOfVisaApplication'
  | 'livedInUKLast12Months'
  | 'hasOtherNationality',
  boolean
>;

type Mandatory = Record<
  | 'gender'
  | 'dob'
  | 'birthCountry'
  | 'nationality'
  | 'appliedForEUSS'
  | 'grantedEUSSStatus'
  | 'isCountryOfNationality'
  | 'countryOfVisaApplication'
  | 'livedInUKLast12Months'
  | 'hasOtherNationality',
  boolean
>;

export function getPageStatus(
  config: StudentState['config'],
  fields: Fields,
): {
  complete: boolean;
  visible: typeof Visible;
} {
  const {
    gender,
    dob,
    birthCountry,
    nationality,
    appliedForEUSS,
    grantedEUSSStatus,
    hasOtherNationality,
    applyingFromHomeCountry,
    countryOfVisaApplication,
    livedInUKLast12Months,
    otherCountryReason,
    otherNationalities,
  } = fields;

  const {
    Gender: genderConfig,
    DOB: dobConfig,
    BirthCountry: birthCountryConfig,
    Nationality: nationalityConfig,
    AppliedForEUSS: appliedForEUSSConfig,
    GrantedEUSSStatus: grantedEUSSStatusConfig,
    IsCountryOfNationality: isCountryOfNationalityConfig,
    CountryOfVisaApplication: countryOfVisaApplicationConfig,
    LivedInUKLast12Months: livedInUKLast12MonthsConfig,
    HasOtherNationality: hasOtherNationalityConfig,
  }: {
    [key: string]: any;
  } = config.Checklist.PersonalInfo.Page2;

  const isEUSSNationality =
    countries.find((item: IDataCountry) => item.nationality === nationality)?.category === 'EUSS';

  const visible: typeof Visible = {
    gender: genderConfig?.visible,
    dob: dobConfig?.visible,
    birthCountry: birthCountryConfig?.visible,
    nationality: nationalityConfig?.visible,
    appliedForEUSS: (appliedForEUSSConfig?.visible ?? true) && isEUSSNationality,
    grantedEUSSStatus: (grantedEUSSStatusConfig?.visible ?? true) && appliedForEUSS === AppliedEUSSStatus.GRANTED,
    isCountryOfNationality:
      (isCountryOfNationalityConfig?.visible ?? true) &&
      (!isEUSSNationality || appliedForEUSS === AppliedEUSSStatus.NO),
    countryOfVisaApplication: (countryOfVisaApplicationConfig?.visible ?? true) && applyingFromHomeCountry === false,
    livedInUKLast12Months:
      (countryOfVisaApplicationConfig?.visible ?? true) &&
      applyingFromHomeCountry === false &&
      countryOfVisaApplication === 'United Kingdom',
    hasOtherNationality: hasOtherNationalityConfig?.visible,
  };

  const mandatory: Mandatory = {
    gender: genderConfig?.mandatory ?? true,
    dob: dobConfig?.mandatory ?? true,
    birthCountry: birthCountryConfig?.mandatory ?? true,
    nationality: nationalityConfig?.mandatory ?? true,
    appliedForEUSS: appliedForEUSSConfig?.mandatory ?? true,
    grantedEUSSStatus: grantedEUSSStatusConfig?.mandatory ?? true,
    isCountryOfNationality: isCountryOfNationalityConfig?.mandatory ?? true,
    countryOfVisaApplication: countryOfVisaApplicationConfig?.mandatory ?? true,
    livedInUKLast12Months: livedInUKLast12MonthsConfig?.mandatory ?? true,
    hasOtherNationality: hasOtherNationalityConfig?.mandatory ?? true,
  };

  const allOtherNamesComplete = (amount: number): boolean => {
    let complete = hasOtherNationality ? otherNationalities!.length > 0 : true;
    for (let i = 0; i < amount; i++) {
      complete =
        complete &&
        !!otherNationalities![i] &&
        (otherNationalities![i].nationality !== '' || typeof otherNationalities![i].isCurrent === 'boolean');
    }
    return complete;
  };

  // prettier-ignore
  const complete =
    (!visible.gender || !mandatory.gender || (gender !== '' && (config.otherGenderEnabled || gender !== 'Other'))) &&
    (!visible.dob || !mandatory.dob || dob !== '') &&
    (!visible.birthCountry || !mandatory.birthCountry || birthCountry !== '') &&
    (!visible.nationality || !mandatory.nationality || nationality !== '') &&
    (!visible.hasOtherNationality || !mandatory.hasOtherNationality || hasOtherNationality !== undefined) &&
    (!visible.appliedForEUSS ||
      !mandatory.appliedForEUSS ||
      (isEUSSNationality && appliedForEUSS !== undefined) ||
      !isEUSSNationality) &&
    (!visible.grantedEUSSStatus ||
      !mandatory.grantedEUSSStatus ||
      (appliedForEUSS === AppliedEUSSStatus.GRANTED && grantedEUSSStatus !== undefined) ||
      [AppliedEUSSStatus.WILL_APPLY, AppliedEUSSStatus.APPLIED, AppliedEUSSStatus.NO].includes(
        appliedForEUSS as AppliedEUSSStatus
      )) &&
    (!visible.isCountryOfNationality ||
      !mandatory.isCountryOfNationality ||
      ((!isEUSSNationality || appliedForEUSS === AppliedEUSSStatus.NO) && applyingFromHomeCountry !== undefined) ||
      (isEUSSNationality &&
        [AppliedEUSSStatus.GRANTED, AppliedEUSSStatus.APPLIED, AppliedEUSSStatus.WILL_APPLY].includes(
          appliedForEUSS as AppliedEUSSStatus
        )) ||
      (applyingFromHomeCountry === false && otherCountryReason !== '') ||
      applyingFromHomeCountry === true) &&
    (!visible.countryOfVisaApplication || !mandatory.countryOfVisaApplication || countryOfVisaApplication !== '') &&
    (!visible.livedInUKLast12Months ||
      !mandatory.livedInUKLast12Months ||
      (applyingFromHomeCountry === false &&
        countryOfVisaApplication === 'United Kingdom' &&
        livedInUKLast12Months !== undefined)) &&
    allOtherNamesComplete(Number(otherNationalities!.length));

  return {
    complete,
    visible,
  };
}
export const appliedForEUSSValue = (appliedForEUSS: AppliedEUSSStatus) => {
  switch (appliedForEUSS) {
    case AppliedEUSSStatus.GRANTED:
      return 'Granted';
    case AppliedEUSSStatus.APPLIED:
      return 'Applied';
    case AppliedEUSSStatus.WILL_APPLY:
      return 'Will Apply';
    case AppliedEUSSStatus.NO:
      return 'No';
    default:
      return '';
  }
};

export const OtherGenderErrorMsg =
  'Unfortunately, it is a UKVI requirement that you must select either Male or Female for your CAS application.';
